import { useEffect, useState } from "react"
import { getUsageData, getTimeSeriesOfApiKey } from "../../services/api"
import {
    Button,
    Skeleton,
    Typography,
    Box,
    Select,
    MenuItem,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Table,
    Chip,
    Tooltip,
    styled,
} from "@mui/material"
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded"
// import BoltRoundedIcon from "@mui/icons-material/BoltRounded"
// import HubRoundedIcon from "@mui/icons-material/HubRounded"
// import CircleRoundedIcon from "@mui/icons-material/CircleRounded"
import { useNavigate } from "react-router-dom"
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Bolt, Circle, Download, Network } from "lucide-react";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

// ---- Design tokens -------------------------------------------------------
const colors = {
    text: "#121417",
    textSecondary: "#6B7280",
    border: "#E4E7EC",
    surface: "#FFFFFF",
    canvas: "#F9FAFB",
    accent: "#3B5BFD",
    accentSoft: "rgba(59, 91, 253, 0.10)",
    success: "#1A9E5A",
    successSoft: "rgba(26, 158, 90, 0.10)",
    danger: "#D92D20",
    dangerSoft: "rgba(217, 45, 32, 0.08)",
};

// ---- Layout primitives ----------------------------------------------------
const Page = styled('div')({
    color: colors.text,
    maxWidth: 1180,
    margin: "0 auto",
    padding: "32px 24px 64px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
});

const PageHeader = styled('div')({
    marginBottom: 28,
});

const StatGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginBottom: 24,
    '@media (max-width: 720px)': {
        gridTemplateColumns: '1fr',
    },
});

const StatCard = styled('div')({
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: "20px 22px",
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
});

const StatIconWrap = styled('div')(({ tint }) => ({
    width: 34,
    height: 34,
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: tint || colors.accentSoft,
}));

const Card = styled('div')({
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: 24,
    marginBottom: 20,
});

const CardHeaderRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 18,
});

const ControlLabel = styled('label')({
    fontSize: 13,
    color: colors.textSecondary,
    marginRight: 8,
});

const selectSx = {
    minWidth: 128,
    borderRadius: 2,
    fontSize: 14,
    background: colors.canvas,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
};

const NotFoundContainer = styled('div')({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    color: "#555",
    gap: 12
})

const bucketMap = {
    minute: 60,
    "5min": 300,
    hour: 3600,
    day: 86400,
};

const thresholdMap = {
    "1h": 3600,
    "2h": 7200,
    "6h": 21600,
    "12h": 43200,
    "24h": 86400,
    "7d": 604800,
    "30d": 2592000
};

const chartOptions = {
    responsive: true,
    aspectRatio: 3.2,
    plugins: {
        legend: { display: false },
        tooltip: {
            enabled: true,
            backgroundColor: colors.text,
            padding: 10,
            cornerRadius: 8,
            titleFont: { size: 12 },
            bodyFont: { size: 12 },
        },
    },
    scales: {
        x: {
            display: true,
            grid: { display: false },
            ticks: { autoSkip: true, maxTicksLimit: 8, color: colors.textSecondary, font: { size: 11 } },
        },
        y: {
            beginAtZero: true,
            grid: { color: "#F0F1F3" },
            ticks: { color: colors.textSecondary, font: { size: 11 } },
        },
    },
};

const Dashboard = () => {

    const [usage, setUsage] = useState()
    const [loading, setLoading] = useState(false)
    const [chartLoading, setChartLoading] = useState(false)
    const [bucket, setBucket] = useState("day");
    const [threshold, setThreshold] = useState("7d");
    const [data, setData] = useState()

    const navigate = useNavigate()

    const fetchData = async () => {
        setLoading(true)
        let data = await getUsageData()
        setLoading(false)
        setUsage(data?.data)
    }

    const fetchDataTimeseries = async (bucketKey = "5min", thresholdKey = "2h") => {
        try {
            setChartLoading(true);
            const resp = await getTimeSeriesOfApiKey(
                bucketMap[bucketKey],
                thresholdMap[thresholdKey]
            );
            setData(resp.data);
        } catch (err) {
            console.error("Error fetching timeseries:", err);
        } finally {
            setChartLoading(false);
        }
    };

    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => {
        fetchDataTimeseries(bucket, threshold);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bucket, threshold])

    const chartData = data?.dataSet
        ? {
            labels: data.dataSet.map((b) => [
                new Date(b.startTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                new Date(b.startTime).toLocaleDateString([], {
                    day: "2-digit",
                    month: "2-digit",
                })
            ]),
            datasets: [
                {
                    label: "Actions",
                    data: data.dataSet.map((b) => b.count),
                    fill: true,
                    tension: 0.3,
                    borderColor: colors.accent,
                    backgroundColor: (ctx) => {
                        const { chart } = ctx;
                        const { chartArea } = chart;
                        if (!chartArea) return colors.accentSoft;
                        const gradient = chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                        gradient.addColorStop(0, "rgba(59, 91, 253, 0.22)");
                        gradient.addColorStop(1, "rgba(59, 91, 253, 0.00)");
                        return gradient;
                    },
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    pointHoverBackgroundColor: colors.accent,
                    borderWidth: 2,
                },
            ],
        }
        : null;

    const exportCsv = () => {
        if (!data?.dataSet) return;
        const rows = [
            ["Start Time", "End Time", "Count"],
            ...data.dataSet.map((b) => [b.startTime, b.endTime, b.count]),
        ];
        const csvContent =
            "data:text/csv;charset=utf-8," +
            rows.map((e) => e.join(",")).join("\n");
        const link = document.createElement("a");
        link.href = encodeURI(csvContent);
        link.download = "timeseries.csv";
        link.click();
    };

    const formatSize = (kb) => {
        if (kb == null) return null
        if (kb < 1) return `${Math.round(kb * 1000)} B`
        if (kb < 1024) return `${kb.toFixed(kb < 10 ? 2 : 1)} KB`
        return `${(kb / 1024).toFixed(2)} MB`
    }

    const methodColor = (method) => {
        switch ((method || "").toUpperCase()) {
            case "GET": return { color: "#1A5FD9", bg: "rgba(26,95,217,0.08)" };
            case "POST": return { color: colors.success, bg: colors.successSoft };
            case "PUT":
            case "PATCH": return { color: "#B15400", bg: "rgba(177,84,0,0.08)" };
            case "DELETE": return { color: colors.danger, bg: colors.dangerSoft };
            default: return { color: colors.textSecondary, bg: "#F2F3F5" };
        }
    };

    if (!loading && !usage) return (
        <NotFoundContainer>
            <Typography variant="h5" fontWeight={600}>No API Key Found</Typography>
            <Typography variant="body1" sx={{ opacity: 0.7 }}>
                Generate your API key
            </Typography>
            <Button
                variant="contained"
                sx={{
                    background: colors.text,
                    color: "#fff",
                    borderRadius: 2,
                    textTransform: "none",
                    mt: 1,
                    px: 3,
                    "&:hover": { background: "#333" }
                }}
                onClick={() => navigate("/api-keys")}
            >
                Generate Now
            </Button>
        </NotFoundContainer>
    )

    return (
        <Page>
            <PageHeader>
                <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: -0.5 }}>Dashboard</Typography>
                <Typography sx={{ fontSize: 14, color: colors.textSecondary, mt: 0.5 }}>
                    An overview of your API usage
                </Typography>
            </PageHeader>

            <StatGrid>
                <StatCard>
                    <StatIconWrap>
                        <Bolt sx={{ fontSize: 18, color: colors.accent }} />
                    </StatIconWrap>
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>API Usage</Typography>
                    {loading ? (
                        <Skeleton variant="text" width={80} height={36} />
                    ) : (
                        <Typography sx={{ fontSize: 26, fontWeight: 700 }}>
                            {usage?.usageCount?.toLocaleString?.() ?? usage?.usageCount ?? "0"}
                        </Typography>
                    )}
                </StatCard>

                <StatCard>
                    <StatIconWrap tint="rgba(177,84,0,0.08)">
                        <Network sx={{ fontSize: 18, color: "#B15400" }} />
                    </StatIconWrap>
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>Total Venues</Typography>
                    {loading ? (
                        <Skeleton variant="text" width={80} height={36} />
                    ) : (
                        <Typography sx={{ fontSize: 26, fontWeight: 700 }}>
                            {usage?.totalVenues?.toLocaleString?.() ?? usage?.totalVenues ?? "0"}
                        </Typography>
                    )}
                </StatCard>

                <StatCard>
                    <StatIconWrap tint={usage?.apiStatus ? colors.successSoft : colors.dangerSoft}>
                        <Circle sx={{ fontSize: 12, color: usage?.apiStatus ? colors.success : colors.danger }} />
                    </StatIconWrap>
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>API Status</Typography>
                    {loading ? (
                        <Skeleton variant="text" width={80} height={36} />
                    ) : (
                        <Typography sx={{ fontSize: 26, fontWeight: 700, color: usage?.apiStatus ? colors.success : colors.danger }}>
                            {usage?.apiStatus || "Unknown"}
                        </Typography>
                    )}
                </StatCard>
            </StatGrid>

            <Card>
                <CardHeaderRow>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Requests over time</Typography>
                    </Box>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <Box display="flex" alignItems="center">
                            <ControlLabel>Bucket</ControlLabel>
                            <Select
                                size="small"
                                value={bucket}
                                onChange={(e) => setBucket(e.target.value)}
                                sx={selectSx}
                            >
                                <MenuItem value="minute">1 minute</MenuItem>
                                <MenuItem value="5min">5 minutes</MenuItem>
                                <MenuItem value="hour">1 hour</MenuItem>
                                <MenuItem value="day">1 day</MenuItem>
                            </Select>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <ControlLabel>Range</ControlLabel>
                            <Select
                                size="small"
                                value={threshold}
                                onChange={(e) => setThreshold(e.target.value)}
                                sx={selectSx}
                            >
                                <MenuItem value="1h">Last 1 hour</MenuItem>
                                <MenuItem value="2h">Last 2 hours</MenuItem>
                                <MenuItem value="6h">Last 6 hours</MenuItem>
                                <MenuItem value="12h">Last 12 hours</MenuItem>
                                <MenuItem value="24h">Last 24 hours</MenuItem>
                                <MenuItem value="7d">Last 7 days</MenuItem>
                                <MenuItem value="30d">Last 30 days</MenuItem>
                            </Select>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Download sx={{ fontSize: 16 }} />}
                            onClick={exportCsv}
                            disabled={!data?.dataSet?.length}
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                borderColor: colors.border,
                                color: colors.text,
                                "&:hover": { borderColor: colors.text, background: colors.canvas },
                            }}
                        >
                            Export CSV
                        </Button>
                    </Box>
                </CardHeaderRow>

                {chartLoading ? (
                    <Skeleton variant="rounded" height={220} />
                ) : chartData && data.dataSet.length > 0 ? (
                    <Line data={chartData} options={chartOptions} />
                ) : (
                    <Box sx={{
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: colors.textSecondary,
                        fontSize: 14,
                        background: colors.canvas,
                        borderRadius: 2,
                    }}>
                        No requests in this time range
                    </Box>
                )}
            </Card>

            <Card sx={{ padding: 0 }}>
                <Box sx={{ padding: "24px 24px 0" }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>Recent API calls</Typography>
                </Box>
                <TableContainer component={Paper} elevation={0} sx={{ boxShadow: "none" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: colors.textSecondary, fontSize: 12, fontWeight: 600, borderColor: colors.border }}>URL</TableCell>
                                <TableCell sx={{ color: colors.textSecondary, fontSize: 12, fontWeight: 600, borderColor: colors.border }}>Body</TableCell>
                                <TableCell sx={{ color: colors.textSecondary, fontSize: 12, fontWeight: 600, borderColor: colors.border }}>Response Size</TableCell>
                                <TableCell sx={{ color: colors.textSecondary, fontSize: 12, fontWeight: 600, borderColor: colors.border }}>Method</TableCell>
                                <TableCell sx={{ color: colors.textSecondary, fontSize: 12, fontWeight: 600, borderColor: colors.border }}>Created</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {!data?.apiCalls?.length && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 5, color: colors.textSecondary, border: "none" }}>
                                        No API calls recorded yet
                                    </TableCell>
                                </TableRow>
                            )}
                            {data?.apiCalls?.map((call) => {
                                const m = methodColor(call.requestMethod);
                                const bodyStr = JSON.stringify(call?.body ?? {});
                                return (
                                    <TableRow key={call._id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5, borderColor: colors.border }}>
                                            {call.requestString}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5, borderColor: colors.border, maxWidth: 260 }}>
                                            <Tooltip title={bodyStr} placement="top-start">
                                                <span style={{
                                                    display: "block",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}>
                                                    {bodyStr}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12.5, color: colors.textSecondary, borderColor: colors.border, whiteSpace: "nowrap" }}>
                                            {formatSize(call?.responsePayloadSizeInKb)}
                                        </TableCell>
                                        <TableCell sx={{ borderColor: colors.border }}>
                                            <Chip
                                                label={call.requestMethod}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: m.color,
                                                    background: m.bg,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12.5, color: colors.textSecondary, borderColor: colors.border, whiteSpace: "nowrap" }}>
                                            {new Date(call.createdAt).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Page>
    )
}

export default Dashboard